'use client';

import { c as _c } from "react/compiler-runtime";
import type { HeroState } from './utils';
import type React from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FlexColumn, FlexRow } from 'src/components/Atoms/FlexBox';
interface HeroHeaderProps {
  heroState: HeroState;
  firstName: string;
  teamName: string;
  goalLabel: string | null;
  displayProgress: number | null;
}
const Highlight = styled('span')(({
  theme
}) => ({
  color: theme.colors.text.brand
}));
const Headline: React.FC<Omit<HeroHeaderProps, 'firstName'>> = t0 => {
  const $ = _c(17);
  const {
    heroState,
    teamName,
    goalLabel,
    displayProgress
  } = t0;
  let t1;
  if ($[0] !== goalLabel) {
    t1 = goalLabel ? goalLabel.toLowerCase() : "goals";
    $[0] = goalLabel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const goal = t1;
  if (heroState === "loading") {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span aria-hidden={true}><Skeleton variant="text" width="75%" height={56} /><Skeleton variant="text" width="50%" height={56} /></span>;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (heroState === "no-connections") {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Highlight>Connect your platforms</Highlight>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    const t3 = ` to see how ${teamName} is tracking toward its ${goal}`;
    let t4;
    if ($[4] !== t3) {
      t4 = <>{t2}{t3}</>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  if (heroState === "no-data") {
    let t2;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <>{"Your platforms are connected \u2014 "}<Highlight>the first numbers</Highlight>{" are on their way"}</>;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  if (displayProgress === null) {
    const t2 = `Here's how ${teamName} is doing`;
    let t3;
    if ($[7] !== t2) {
      t3 = <>{t2}</>;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  const t2 = `${teamName} is `;
  let t3;
  if ($[9] !== displayProgress) {
    t3 = Math.round(displayProgress);
    $[9] = displayProgress;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = `${t3}%`;
  let t5;
  if ($[11] !== t4) {
    t5 = <Highlight>{t4}</Highlight>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = ` of the way to its ${goal}`;
  let t7;
  if ($[13] !== t2 || $[14] !== t5 || $[15] !== t6) {
    t7 = <>{t2}{t5}{t6}</>;
    $[13] = t2;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
};
const HeroHeader: React.FC<HeroHeaderProps & {
  connectionsSlot?: React.ReactNode;
  onConnect: () => void;
  platformNames: string[];
}> = t0 => {
  const $ = _c(16);
  const {
    heroState,
    firstName,
    teamName,
    goalLabel,
    displayProgress,
    connectionsSlot,
    onConnect,
    platformNames
  } = t0;
  const t1 = `Welcome back, ${firstName}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <Typography variant="body-sm" sx={_temp2}>{t1}</Typography>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== displayProgress || $[3] !== goalLabel || $[4] !== heroState || $[5] !== teamName) {
    t3 = <Typography variant="heading-4xl" component="h1" sx={_temp3}><Headline heroState={heroState} teamName={teamName} goalLabel={goalLabel} displayProgress={displayProgress} /></Typography>;
    $[2] = displayProgress;
    $[3] = goalLabel;
    $[4] = heroState;
    $[5] = teamName;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== heroState || $[8] !== onConnect || $[9] !== platformNames) {
    t4 = heroState === "no-connections" && <FlexColumn sx={_temp4}><Button variant="contained" onClick={onConnect}>Connect a platform</Button><Typography variant="body-sm" sx={_temp5}>Takes about 2 minutes. Data flows in automatically.</Typography>{platformNames.length > 0 && <FlexRow sx={_temp6}>{platformNames.map(_temp7)}</FlexRow>}<Typography variant="body-xs" sx={_temp8}>{`0 of ${platformNames.length} platforms connected`}</Typography></FlexColumn>;
    $[7] = heroState;
    $[8] = onConnect;
    $[9] = platformNames;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = heroState === "no-data" && connectionsSlot;
  let t6;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5) {
    t6 = <FlexColumn sx={_temp}>{t2}{t3}{t4}{t5}</FlexColumn>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
export default HeroHeader;
function _temp(theme) {
  return {
    gap: theme.sizes.spacing["07"]
  };
}
function _temp2(theme_0) {
  return {
    color: theme_0.colors.text.tertiary
  };
}
function _temp3(theme_1) {
  return {
    color: theme_1.colors.text.primary,
    maxWidth: "40rem"
  };
}
function _temp4(theme_2) {
  return {
    gap: theme_2.sizes.spacing["06"],
    alignItems: "flex-start"
  };
}
function _temp5(theme_3) {
  return {
    color: theme_3.colors.text.tertiary
  };
}
function _temp6(theme_4) {
  return {
    flexWrap: "wrap",
    gap: theme_4.sizes.spacing["04"]
  };
}
function _temp7(name) {
  return <Chip key={name} size="small" variant="outlined" label={name} />;
}
function _temp8(theme_5) {
  return {
    color: theme_5.colors.text.tertiary
  };
}